import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { useLoading } from "../context/LoadingContext";
import { useRef } from "react";

interface RequestOptions extends AxiosRequestConfig {
  // Extend AxiosRequestConfig with any additional properties you need
}

// Create an Axios instance with default configuration
const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding authorization token from cookie to requests
axiosInstance.interceptors.request.use(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (config: any) => {
    const token = Cookies.get("auth_token");
    const userID = Cookies.get("userAccessId");
    const roleID = Cookies.get("roleId");
    if (token && userID && roleID) {
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
        Roleid: `${roleID}`,
        Userid: `${userID}`,
      };
    }

    return config;
  },
  (error) => {
    // console.error("Error in request interceptor:", error);
    toast.error("Request error: Unable to send the request.");
    return Promise.reject(error);
  },
);

// Response interceptor for handling responses globally
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => response.data,
  (error) => {
    // console.error("Error in response interceptor:", error);
    const statusCode = error.response?.status;
    switch (statusCode) {
      case 400:
        toast.error(
          error.response?.data.message ??
            "Bad Request: The server could not understand the request due to invalid syntax.",
        );
        break;
      case 401:
        toast.error(
          error.response?.data.message ?? "Unauthorized: Access is denied due to invalid credentials.",
        );

        break;
      case 403:
        toast.error(
          error.response?.data.message ?? "Forbidden: You do not have permission to access this resource.",
        );
        break;
      case 404:
        toast.error(error.response?.data.message ?? "Not Found: The requested resource could not be found.");
        break;
      case 500:
        toast.error(
          error.response?.data.message ??
            "Internal Server Error: The server encountered an error and could not complete your request.",
        );
        break;
      case 502:
        toast.error("Bad Gateway: The server received an invalid response from the upstream server.");
        break;
      case 503:
        toast.error("Service Unavailable: The server is not ready to handle the request.");
        break;
      case 504:
        toast.error(
          "Gateway Timeout: The server did not receive a timely response from the upstream server.",
        );
        break;
      default:
        toast.error("An unexpected error occurred.");
        break;
    }

    if (error.response.status === 401) {
      const allCookies = Cookies.get(); // Get all cookies
      for (const cookie in allCookies) {
        Cookies.remove(cookie); // Remove each cookie
      }
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    }

    return Promise.reject(error);
  },
);

export const useApiRequest = () => {
  const { setLoading } = useLoading(); // Use the loading context

  const ongoingRequests = useRef<{ [key: string]: CancelTokenSource }>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pendingRequests = useRef<Set<Promise<any>>>(new Set());

  const getRequestKey = (url: string, method: string) => `${method.toUpperCase()}_${url}`;

  const fetchTokenData = async (url: string, options: RequestOptions = {}, trackGlobalLoading = true) => {
    const { method = "GET", data, headers = {}, ...rest } = options;
    const requestKey = getRequestKey(url, method);

    if (ongoingRequests.current[requestKey]) {
      return; // Exit early if a request with the same URL and method is already in progress
    }

    const newCancelTokenSource = axios.CancelToken.source();
    ongoingRequests.current[requestKey] = newCancelTokenSource;

    const requestPromise = axiosInstance({
      url,
      method,
      headers,
      data,
      cancelToken: newCancelTokenSource.token,
      ...rest,
    }).finally(() => {
      delete ongoingRequests.current[requestKey]; // Remove from ongoing requests on response (success or error)
    });

    try {
      const response = await requestPromise;
      return response;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: unknown) {
      // if (!error.response || error.response.status === 401 || error.response.status === 404 || error.response.status === 500) {
      //   const allCookies = Cookies.get(); // Get all cookies
      //   for (const cookie in allCookies) {
      //     Cookies.remove(cookie); // Remove each cookie
      //   }
      //   window.location.href = "/login";
      // }
      // Handle error as needed
    } finally {
      if (trackGlobalLoading) {
        pendingRequests.current.delete(requestPromise);
        if (pendingRequests.current.size === 0) {
          // setLoading(false); // Stop the global loader if no pending requests
        }
      }
    }
  };

  const fetchData = async (url: string, options: RequestOptions = {}, trackGlobalLoading = true) => {
    const { method = "GET", data, headers = {}, ...rest } = options;
    const requestKey = getRequestKey(url, method);

    if (ongoingRequests.current[requestKey]) {
      return; // Exit early if a request with the same URL and method is already in progress
    }

    const newCancelTokenSource = axios.CancelToken.source();
    ongoingRequests.current[requestKey] = newCancelTokenSource;

    const requestPromise = axiosInstance({
      url,
      method,
      headers,
      data,
      cancelToken: newCancelTokenSource.token,
      ...rest,
    }).finally(() => {
      delete ongoingRequests.current[requestKey]; // Remove from ongoing requests on response (success or error)
    });

    if (trackGlobalLoading) {
      pendingRequests.current.add(requestPromise);
      setLoading(true, "Loading..."); // Start the global loader
    }

    try {
      const response = await requestPromise;
      return response as typeof response;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: unknown) {
      // if (error.response.status === 401) {
      //   const allCookies = Cookies.get(); // Get all cookies
      //   for (const cookie in allCookies) {
      //     Cookies.remove(cookie); // Remove each cookie
      //   }
      //   toast.error(error.response?.data.message);
      // }
      // Handle error as needed
    } finally {
      if (trackGlobalLoading) {
        pendingRequests.current.delete(requestPromise);
        if (pendingRequests.current.size === 0) {
          setLoading(false); // Stop the global loader if no pending requests
        }
      }
    }
  };

  const fetchDataExport = async (url: string, options: RequestOptions = {}, trackGlobalLoading = true) => {
    const { method = "GET", data, headers = {}, ...rest } = options;
    const requestKey = getRequestKey(url, method); // Include data in the key

    const newCancelTokenSource = axios.CancelToken.source();
    ongoingRequests.current[requestKey] = newCancelTokenSource;

    const requestPromise = axiosInstance({
      url,
      method,
      headers,
      data,
      cancelToken: newCancelTokenSource.token,
      ...rest,
    }).finally(() => {
      // delete ongoingRequests.current[requestKey]; // Remove from ongoing requests on response (success or error)
    });

    if (trackGlobalLoading) {
      pendingRequests.current.add(requestPromise);
      setLoading(true, "Loading..."); // Start the global loader
    }

    try {
      const response = await requestPromise;
      return response;
    } catch (error: unknown) {
      console.error("API request failed", error);
      throw error; // Re-throw error so the calling function can handle it
    } finally {
      if (trackGlobalLoading) {
        pendingRequests.current.delete(requestPromise);
        if (pendingRequests.current.size === 0) {
          setLoading(false); // Stop the global loader if no pending requests
        }
      }
    }
  };

  const fetchsearchNameData = async (url: string, options: RequestOptions = {}) => {
    const { method = "GET", data, headers = {}, ...rest } = options;
    const requestKey = getRequestKey(url, method);

    if (ongoingRequests.current[requestKey]) {
      return; // Exit early if a request with the same URL and method is already in progress
    }

    const newCancelTokenSource = axios.CancelToken.source();
    ongoingRequests.current[requestKey] = newCancelTokenSource;

    try {
      const response = await axiosInstance({
        url,
        method,
        headers,
        data,
        cancelToken: newCancelTokenSource.token,
        ...rest,
      });
      delete ongoingRequests.current[requestKey]; // Remove from ongoing requests on successful response

      return response;
    } catch (error) {
      delete ongoingRequests.current[requestKey]; // Remove from ongoing requests on error
      console.error("Error fetching data:", error);
      throw error;
    }
  };

  const waitForAllRequests = async () => {
    await Promise.all(pendingRequests.current);
    setLoading(false); // Stop the loader after all requests have completed
  };

  // Function to cancel the request
  const cancelRequest = (url: string, method: string) => {
    const requestKey = getRequestKey(url, method);
    if (ongoingRequests.current[requestKey]) {
      ongoingRequests.current[requestKey].cancel("Request canceled by user.");
      delete ongoingRequests.current[requestKey];
    }
  };

  return {
    fetchData,
    fetchTokenData,
    fetchsearchNameData,
    cancelRequest,
    waitForAllRequests,
    fetchDataExport,
  };
};

export { axiosInstance };
// Function to set authentication token in cookies
export const setAuthTokenInCookie = (token: string) => {
  Cookies.set("auth_token", token, { expires: 2 }); // Set cookie expiration (2 days in this example)
};

export const setCookieData = (key: string, value: string) => {
  Cookies.set(key, value, { expires: 3 }); // Set cookie expiration (2 days in this example)
};

export const getCookieData = (key: string) => {
  return Cookies.get(key);
};

export const removeCookieData = (key: string) => {
  return Cookies.remove(key);
};

export const setEmployeeID = (id: number) => {
  Cookies.set("employeeId", id.toString(), { expires: 2 }); // Set cookie expiration (2 days in this example)
};

export const getEmployeeID = () => {
  return Cookies.get("employeeId");
};

export const setUserIdTokenInCookie = (userId: string) => {
  Cookies.set("userId", userId, { expires: 2 }); // Set cookie expiration (2 days in this example)
};

export const getUserIdToken = () => {
  return Cookies.get("userId");
};

export const getGetRole = () => {
  return Cookies.get("roleId");
};

export const getUserIdTokenForIonTime = () => {
  return Cookies.get("iontime_id");
};

export const getUserIdTokenForIPMS = () => {
  return Cookies.get("ipms_id");
};

export const getUserIdTokenForIPMSEmployid = () => {
  return Cookies.get("employee_ID");
};

export const removeAllCookie = () => {
  const allCookies = Cookies.get(); // Get all cookies
  for (const cookie in allCookies) {
    Cookies.remove(cookie); // Remove each cookie
  }
};

// Function to remove authentication token from cookies (logout)
export const removeAuthTokenFromCookie = () => {
  Cookies.remove("auth_token");
  Cookies.remove("userId");
};
